import {Component, HostBinding, Input} from '@angular/core';
import {concatMap, filter} from "rxjs/operators";
import {Router} from "@angular/router";
import {Localization} from '../../../../common/enums/localization';
import {InterventionTeamResource} from "../../intervention-team.resource";
import {InterventionTeamContactResource} from "../../contacts/intervention-team-contact-resource.service";
import {EditNameData, EditNameDialogComponent} from "../edit-name-dialog/edit-name-dialog.component";
import {InterventionTeamContactDto} from "../../contacts/intervention-team-contact.dto";
import {JobsLoginStatus} from "../../../../common/enums/enums";
import {ConfirmationDialogModel, ConfirmDialogType, DialogService} from "@npm.chapps.com/chapps.common.angular.library";
import {TranslateService} from "@ngx-translate/core";
import {Observable} from "rxjs";
import {UntypedFormControl} from "@angular/forms";
import {IdAndLabel} from "../../../../common/model/id-and-label";
import {pluck} from "../../../../../helpers/collections";
import {sortBy} from "../../../../../helpers/sorting";
import {Operation} from "../../../../../helpers/operations";
import {TeamContactDialogComponent} from "../../contacts/new-team-contact-dialog/team-contact-dialog.component";
import {BuildingsService} from "../../../../common/services/buildings.service";
import {BuildingSummary} from "../../../../common/model/building-summary";
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {InterventionTeamDetailDto} from '../intervention-team-detail.dto';
import {InterventionTeamManagerComponent} from '../intervention-team-manager/intervention-team-manager.component';
import {InterventionTeamManagerState} from '../intervention-team-manager/intervention-team-manager.state';


class EditorOperations {
  public readonly loadContacts = new Operation();
  public readonly loadTeamDetails = new Operation();
  public readonly loadBuildings = new Operation();
}

@Component({
  selector: 'app-intervention-team-editor',
  templateUrl: './intervention-team-editor.component.html',
  styleUrls: ['./intervention-team-editor.component.scss']
})
export class InterventionTeamEditorComponent {

  @Input()
  public set id(guid: string) {
     this.fetchTeamDetails(guid)
       .then(async () => {
         await this.determineAvailableContacts();
         await this.determineAvailableBuildings();
       });
  }

  public team: InterventionTeamDetailDto;

  public readonly contactSelectionField = new UntypedFormControl();
  public availableContacts: IdAndLabel[];

  public readonly buildingSelectionField = new UntypedFormControl();
  public availableBuildings: BuildingSummary[];

  public readonly operations = new EditorOperations();
  public teamBuildings: BuildingSummary[];
  protected readonly Localization = Localization;
  private teamContacts: InterventionTeamContactDto[];
  private manager: InterventionTeamManagerComponent;

  constructor(
    private readonly teamResource: InterventionTeamResource,
    private readonly buildingResource: BuildingsService,
    private readonly teamContactsResource: InterventionTeamContactResource,
    private readonly dialogs: MatDialog,
    private readonly prompts: DialogService,
    private readonly routers: Router,
    private readonly translate: TranslateService,
    private readonly managerState: InterventionTeamManagerState,
  ) { }

  @HostBinding('class.disabled')
  public get disabled() {
    return this.operations.loadTeamDetails.busy;
  }

  public get hasContactsAssigned(): boolean {
    return this.team?.assignedContacts?.length > 0;
  }

  public get hasPendingActions(): boolean {
    return this.team?.numberOfActiveActions > 0;
  }

  public get cannotDeleteTeam(): boolean {
    return this.hasPendingActions || this.hasContactsAssigned;
  }

  public get reasonWhyTeamCannotBeDeleted(): string {
    return this.hasContactsAssigned ? Localization.CannotDeleteInterventionTeamMessage : Localization.CannotDeleteInterventionTeamDueToAssignedActions;
  }

  public showEditNameDialog(): void {
    const data = {team: this.team} as EditNameData;
    const dialog: MatDialogRef<EditNameDialogComponent, InterventionTeamDetailDto> = this.dialogs.open(EditNameDialogComponent, {
      panelClass: 'full-bleed',
      data
    });
    dialog.afterClosed().pipe(filter(Boolean)).subscribe((team: InterventionTeamDetailDto) => this.didUpdateTeamName(team));
  }

  public confirmed(contact: InterventionTeamContactDto): any {
    return contact.jobsLoginStatus === JobsLoginStatus.CONFIRMED;
  }

  public invite(contact: InterventionTeamContactDto, $event: MouseEvent): void {
    $event.stopPropagation();
    this.teamContactsResource
      .invite(contact)
      .subscribe(() => contact.jobsLoginStatus = JobsLoginStatus.INVITED);
  }

  public canInvite(contact: InterventionTeamContactDto): boolean {
    return !!contact?.email;
  }

  public show(contact: InterventionTeamContactDto): Promise<boolean> {
    return this.routers.navigate(['/', 'maintenance', 'intervention-teams', 'contacts', contact.id]);
  }

  public delete(): void {
    if (this.cannotDeleteTeam) {
      return console.warn('Prevented deleting team', this.reasonWhyTeamCannotBeDeleted);
    }
    this.confirmDeletion()
      .pipe(
        concatMap(() => this.teamResource.delete(this.team))
      ).subscribe(() => this.managerState.didDeleteTeam(this.team));
  }

  public toggleAutoAccept(isAutoAccept: boolean): void {
    this.team = {...this.team, isAutoAccept};
    this.teamResource.update(this.team).subscribe();
  }

  public async addSelectedContact(): Promise<void> {
    const contact = this.teamContacts.find(({id}) => id === this.contactSelectionField.value);
    await this.assignContactToTeam(contact);
  }

  public addNewContact(): void {
    this.dialogs.open(TeamContactDialogComponent, {panelClass: 'full-bleed'})
      .afterClosed().pipe(
      concatMap(contact => this.assignContactToTeam(contact))
    ).subscribe()
  }

  public removeFromTeam(contact: InterventionTeamContactDto, clickEvent: MouseEvent): void {
    clickEvent.stopPropagation();
    this.confirmRemoval(contact).pipe(
      concatMap(() => this.teamResource.unassign(this.team, contact))
    )
      .subscribe(() => this.didRemoveFromTeam(contact))
  }

  public addSelectedBuilding(): void {
    this.teamResource
      .addBuilding(this.buildingSelectionField.value, this.team)
      .subscribe((building) => this.didAddBuilding(building));
  }

  public showBuilding(building: BuildingSummary): void {

  }

  public removeBuilding(building: BuildingSummary, $event: MouseEvent): void {
    this.teamResource
      .removeBuilding(building, this.team)
      .subscribe(() => this.didRemoveBuilding(building));

  }

  public toggleLinkedToAllBuildings(isLinkedToAllBuildings: boolean): void {
    this.team = {...this.team, isLinkedToAllBuildings};
    this.teamResource.update(this.team).subscribe();
  }

  private async assignContactToTeam(contact: InterventionTeamContactDto): Promise<void> {
    await this.operations.loadTeamDetails.of(this.teamResource.assign(contact, this.team).toPromise());
    this.team.assignedContacts.push(contact);
    this.team.assignedContacts.sort(sortBy('lastName'));
    await this.determineAvailableContacts();
    this.contactSelectionField.reset();
  }

  private confirmDeletion(): Observable<boolean> {
    return this.prompts.confirm(ConfirmationDialogModel.create({
      type: ConfirmDialogType.Danger,
      icon: 'fa-exclamation-triangle',
      text: this.translate.instant(Localization.AreYouSureYouWantToDelete, {value: this.team.label}),
      title: Localization.DeleteTeam,
    })).pipe(filter(v => !!v));
  }

  private async fetchTeamDetails(id: string): Promise<void> {
    const teamDetailsRequest = this.teamResource.get(id).toPromise();
    this.team = await this.operations.loadTeamDetails.of(teamDetailsRequest);
  }

  private didUpdateTeamName(team: InterventionTeamDetailDto): void {
    this.team.label = team.label;
    this.managerState.didUpdateTeam(team);
  }

  private async fetchTeamContacts(): Promise<void> {
    this.teamContacts = (await this.teamContactsResource.get().toPromise()).items;
  }

  private async determineAvailableContacts(): Promise<void> {
    if (!this.teamContacts) {
      await this.operations.loadContacts.of(this.fetchTeamContacts());
    }
    const assignedContactIds = this.team.assignedContacts.map(pluck('id'));
    this.availableContacts = this.teamContacts.filter(({id}) => !assignedContactIds.includes(id))
      .map(({id, firstName, lastName}) => new IdAndLabel(id, `${firstName} ${lastName}`))
      .sort(sortBy('label'));
  }

  private confirmRemoval(contact: InterventionTeamContactDto): Observable<boolean> {
    return this.prompts.confirm(ConfirmationDialogModel.create({
      type: ConfirmDialogType.Danger,
      icon: 'fa-exclamation-triangle',
      text: this.translate.instant(Localization.AreYouSureYouWantToDeleteContactFromThisInterventionTeam,),
      title: Localization.DeleteTeam,
    })).pipe(filter(v => !!v));
  }

  private didRemoveFromTeam(contact: InterventionTeamContactDto): void {
    this.team.assignedContacts = this.team.assignedContacts.filter(({id}) => contact.id !== id);
  }

  private async fetchTeamBuildings(): Promise<BuildingSummary[]> {
    return await this.buildingResource.getAllBuildingsIncludingChildren().toPromise();
  }

  private didAddBuilding(building: BuildingSummary): void {
    this.teamBuildings.push(building);
    this.teamBuildings.sort(sortBy('label'));
    this.availableBuildings = this.availableBuildings.filter(({guid}) => guid !== building.guid);
    this.buildingSelectionField.reset();
  }

  private async determineAvailableBuildings(): Promise<void> {
    const buildings = await this.operations.loadBuildings.of(this.fetchTeamBuildings());
    this.teamBuildings = this.team.buildingIds.map(guid => buildings.find(building => building.guid === guid));
    this.availableBuildings = buildings.filter(({guid}) => !this.team.buildingIds.includes(guid));
  }

  private didRemoveBuilding(building: BuildingSummary): void {
    this.teamBuildings = this.teamBuildings.filter(({guid}) => guid !== building.guid);
    this.availableBuildings = [...this.availableBuildings, building];
    this.availableBuildings.sort(sortBy('label'));
  }
}
